import {
	Divider,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	type StackProps,
} from "@mui/material";
import Box from "@mui/material/Box";
import { HeadingL, HeadingM, Icons } from "@repo/ui";
import { type FC, isValidElement, type ReactElement, type ReactNode } from "react";
import { useDrawer } from "../Drawer/context";
import HeaderWithDescription, {
	type HeaderWithDescriptionProps,
} from "../HeaderWithDescription";

interface IInformationListHeader {
	title?: string;
	action?: ReactNode;
	infoDrawer?: ReactNode;
	largeTitle?: boolean;
}

const InformationListHeader: FC<IInformationListHeader> = ({
	title,
	action,
	infoDrawer,
	largeTitle,
}) => {
	const { showDrawer, setDrawerContent } = useDrawer();
	const TitleComponent = largeTitle ? HeadingL : HeadingM;

	return (
		<Stack
			sx={{
				flexDirection: "row",
				justifyContent: "space-between",
				alignItems: "center",
			}}
		>
			<Stack sx={{ flexDirection: "row", gap: 2 }}>
				{title && (
					<TitleComponent
						withoutScale
						sx={{
							display: infoDrawer ? "block" : "inline",
							verticalAlign: "middle",
							margin: "0px !important",
							whiteSpace: "nowrap",
						}}
					>
						{title}
					</TitleComponent>
				)}
				{infoDrawer && (
					<>
						&nbsp;
						<IconButton
							data-test="info-icon"
							onClick={() => {
								showDrawer();
								setDrawerContent(infoDrawer);
							}}
							sx={{ p: 1 }}
							size="small"
						>
							<Icons.Info
								sx={{ width: "1.8rem", height: "1.8rem" }}
							/>
						</IconButton>
					</>
				)}
			</Stack>
			{action}
		</Stack>
	);
};

export type InformationItem = HeaderWithDescriptionProps | ReactElement | false;

export interface InformationListProps extends StackProps {
	title?: string;
	infoIconDrawer?: ReactElement;
	action?: ReactElement;
	information: InformationItem[];
	isLoading?: boolean;
	secondaryAction?: ReactElement;
	skeletonCount?: number;
	largeTitle?: boolean;
}

const InformationList = ({
	title,
	infoIconDrawer,
	action,
	secondaryAction,
	information,
	isLoading,
	sx,
	skeletonCount,
	largeTitle,
	...rest
}: InformationListProps) => {
	const showTitle = title ?? action;
	return (
		<Stack
			{...rest}
			sx={{ gap: 4, height: { md: "auto", xs: "100%" }, ...sx }}
		>
			{showTitle && !isLoading && (
				<InformationListHeader
					title={title}
					action={action}
					infoDrawer={infoIconDrawer}
					largeTitle={largeTitle}
				/>
			)}
			{showTitle && isLoading && <Skeleton width={250} sx={{ mb: 4 }} />}
			<Paper
				sx={{
					height: "100%",
					p: { md: 5, xs: 4 },
				}}
			>
				<Stack spacing={3} divider={<Divider />}>
					{!isLoading
						? information
							.filter((i) => !!i)
							.map((information, i) =>
								isValidElement(information) ? (
									<div key={i}>
										{information}
									</div>
								) : (
									<HeaderWithDescription
										key={i}
										{...(information as HeaderWithDescriptionProps)}
									/>
								)
							)
						: [...Array(skeletonCount ?? 3)].map((_, i) => (
							<HeaderWithDescription
								key={i}
								title={<Skeleton width="100%" />}
								description={<Skeleton width="100%" />}
							/>
						))}
				</Stack>
				<Box sx={{ alignItems: "end" }}>{secondaryAction}</Box>
			</Paper>
		</Stack>
	);
};

export default InformationList;

export interface InformationListSkeletonProps extends StackProps {
	title?: string;
	length: number;
	action?: ReactElement;
}

export const InformationListSkeleton: FC<InformationListSkeletonProps> = ({
	length,
	title,
	action,
	sx,
}) => {
	return (
		<InformationList
			title={title}
			information={new Array(length).fill({
				title: <Skeleton />,
				description: <Skeleton width={"100%"} />,
			})}
			action={action}
			sx={sx}
		/>
	);
};
